import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import ResourceForm from "./ResourceForm";

import { DataTypes } from "../../../../constants";
import rmsActions from "../../../../actions/rmsActions";
import { getResources, isFetching } from "../../../../reducers";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ResourceIcon from "./ResourceIcon";

function AttachResourceFormDialog({open, attached, resources, onDelete, onSubmit, onSelect, onClose, canUpdate, openUploadDialog, setOpenUploadDialog, appCode, ...props}) {
    const canSubmit = attached.length + resources.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={true}
            maxWidth="md"
            {...props}
        >
            <DialogTitle>Attached resources</DialogTitle>
            <DialogContent dividers={false}>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <List dense={true}>
                        {Array.isArray(attached) &&
                            attached.map((item) => (
                                <ListItem
                                    key={item.id}
                                    secondaryAction={
                                        canUpdate && (
                                            <IconButton
                                                aria-label="Delete"
                                                size="sm"
                                                onClick={() => onDelete(item.id)}
                                            >
                                                <DeleteOutlinedIcon />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemButton component={Link} to={`/portfolio/resources/${item.id}`} >
                                        <ListItemIcon>
                                            <ResourceIcon fileMimeType={item?.fileMimeType} color={"primary"} />
                                        </ListItemIcon>
                                        <ListItemText primary={item.title} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                    </List>

                    {canUpdate && (
                        <>
                            <FormControl
                                component="div"
                                margin={"normal"}
                                fullWidth={true}
                                autoFocus
                            >
                                {resources?.length > 0 ? (
                                    <>
                                        <InputLabel
                                            id="resource-label"
                                            sx={{backgroundColor: "#FFF", padding: "0 4px"}}
                                        >
                                            Resource
                                        </InputLabel>
                                        <Select
                                            id="select-resource"
                                            value=""
                                            onChange={({target}) => onSelect(target.value)}
                                        >
                                            {resources.map((option) => (
                                                <MenuItem value={option.id} key={option.id} >
                                                    {option.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            Select a resource
                                        </FormHelperText>
                                    </>
                                ) : (
                                    <AlertMessage
                                        severity={"info"}
                                        message={"Resource selection empty. Try uploading."}
                                    />
                                )}
                            </FormControl>
                            <Box className="form-row">
                                <Button
                                    variant="contained"
                                    color="neutral"
                                    disableElevation={true}
                                    startIcon={<FileUploadIcon />}
                                    key="upload"
                                    onClick={() => setOpenUploadDialog(true)}
                                >
                                    Upload a file
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>
                {canUpdate && canSubmit && (
                    <Button
                        key="save"
                        onClick={onSubmit}
                        variant="contained"
                        disableElevation={true}
                        color="primary"
                    >
                        Save
                    </Button>
                )}
            </DialogActions>
            {openUploadDialog && (
                <ResourceForm appCode={appCode} onClose={() => setOpenUploadDialog(false)} />
            )}
        </Dialog>
    );
}

function AttachResourceFormDialogContainer({onSave, onClose, data = {}, canUpdate = false, appCode, ...props}) {
    const dispatch = useDispatch();
    const fetching = useSelector((state) => isFetching(state, DataTypes.RESOURCES));
    let resources = useSelector((state) => getResources(state));

    const attached = data?.resources ?? [];

    if (!fetching && resources === null) {
        dispatch(rmsActions.getResources(appCode));
        resources = [];
    }

    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [tempAttached, setTempAttached] = useState(attached);
    const tempAttachedIds = tempAttached.map(({ id }) => Number(id));

    const onSelect = (id) => {
        let newResource = resources.find((newResource) => Number(newResource.id) === Number(id));
        setTempAttached([...tempAttached, newResource]);
    };

    const onDelete = (id) => {
        let filtered = tempAttached.filter((item) => Number(item.id) !== Number(id));
        setTempAttached(filtered);
    };

    const onSubmit = () => {
        onSave(tempAttached);
        onClose();
    };

    return (
        <AttachResourceFormDialog
            resources={!resources ? [] : resources.filter(({id}) => !tempAttachedIds.includes(Number(id)))}
            attached={tempAttached}
            onSelect={onSelect}
            onDelete={onDelete}
            onSubmit={onSubmit}
            onClose={onClose}
            canUpdate={canUpdate}
            openUploadDialog={openUploadDialog}
            setOpenUploadDialog={setOpenUploadDialog}
            scroll="paper"
            appCode={appCode}
            {...props}
        />
    );
}

export default AttachResourceFormDialogContainer;
